import { format, isValid, parse } from "date-fns";

const EXCEL_EPOCH = new Date(1899, 11, 30);

const MONTH_YEAR_FORMATS = [
  "yyyy-MM",
  "yyyy/M",
  "yyyy/MM",
  "MM/yyyy",
  "M/yyyy",
  "MM-yyyy",
  "M-yyyy",
  "MMM yyyy",
  "MMMM yyyy",
  "MMM, yyyy",
  "MMMM, yyyy",
  "MMM yy",
  "MMMM yy",
  "yyyy",
];

function normalizeWhitespace(s: string) {
  return s.replace(/\s+/g, " ").trim();
}

function looksLikeExcelSerial(n: number) {
  return Number.isFinite(n) && n > 1 && n < 100000;
}

/**
 * Normalizes a date-ish value to:
 * - "YYYY-MM" when month is known
 * - "YYYY" when only year is known
 * - "" when unknown/invalid
 */
export function normalizeDateString(value: unknown): string {
  if (value == null) return "";

  // Excel serial dates
  if (typeof value === "number" && looksLikeExcelSerial(value)) {
    const date = new Date(EXCEL_EPOCH.getTime() + value * 86_400_000);
    return format(date, "yyyy-MM");
  }

  // Date object
  if (value instanceof Date && isValid(value)) {
    return format(value, "yyyy-MM");
  }

  const raw = String(value).trim();
  if (!raw) return "";

  // "Present" / "Current" handled by range parser or caller
  if (/^(present|current)$/i.test(raw)) return "";

  // Numeric string could be Excel serial
  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const n = Number(raw);
    if (looksLikeExcelSerial(n)) {
      const date = new Date(EXCEL_EPOCH.getTime() + n * 86_400_000);
      return format(date, "yyyy-MM");
    }
  }

  const cleaned = normalizeWhitespace(raw.replace(/,/g, " "));

  // Year only (keep as YYYY so preview can show just the year)
  const yearOnly = cleaned.match(/^(19|20)\d{2}$/);
  if (yearOnly) return yearOnly[0];

  for (const fmt of MONTH_YEAR_FORMATS) {
    const parsed = parse(cleaned, fmt, new Date());
    if (isValid(parsed)) {
      // If the input is year-only, parse() will set a month/day; keep year-only instead.
      if (fmt === "yyyy") return cleaned;
      return format(parsed, "yyyy-MM");
    }
  }

  // Fallback: if we can at least find a year, return it.
  const anyYear = cleaned.match(/\b(19|20)\d{2}\b/);
  if (anyYear) return anyYear[0];

  return "";
}

export function parseDateRange(value: unknown):
  | { startDate: string; endDate: string; current: boolean }
  | null {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const s = normalizeWhitespace(raw.replace(/[–—−]/g, "-").replace(/,/g, " "));
  const lower = s.toLowerCase();

  // Split patterns we see commonly in resumes
  let parts: string[] = [];
  if (/\s+-\s+/.test(s)) {
    parts = s.split(/\s+-\s+/);
  } else if (/\s+to\s+/i.test(s)) {
    parts = s.split(/\s+to\s+/i);
  }

  if (parts.length !== 2) return null;

  const startRaw = parts[0];
  const endRaw = parts[1];

  const current = /(present|current)/i.test(endRaw);
  const startDate = normalizeDateString(startRaw);
  const endDate = current ? "" : normalizeDateString(endRaw);

  if (!startDate && !endDate && !current) return null;
  return { startDate, endDate, current };
}

export function normalizeWorkType(value: unknown): string {
  const s = String(value ?? "").trim();
  if (!s) return "";

  const lower = s.toLowerCase();
  if (lower.includes("remote")) return "Remote";
  if (lower.includes("hybrid")) return "Hybrid/Remote";
  if (lower.includes("on-site") || lower.includes("onsite") || lower.includes("on site")) return "On-site";
  return s;
}
